import { Switch, type SwitchSize } from '@trustoo/components/Switch/v2'
import React from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

const SWITCH_USAGE = `import { Switch } from '@trustoo/components/Switch/v2'

// Uncontrolled
<Switch defaultChecked onChange={(on) => save(on)} />

// Controlled
<Switch checked={enabled} onChange={setEnabled} />

// Slim rail + small size
<Switch slim size="sm" defaultChecked />

// With label + supporting text
<Switch
  label="Remember me"
  supportingText="Save my login details for next time."
  defaultChecked
/>`

export const SwitchShowcase: React.FC = () => (
    <Section
        title="Switch (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Toggle</span> set
                (ComponentSet <InlineCode>5:44758</InlineCode>) — an on/off pill switch. Coded as{' '}
                <InlineCode>Switch</InlineCode> to avoid colliding with the unrelated{' '}
                <InlineCode>shared/ui/Toggle</InlineCode> accordion. Import v2 explicitly via{' '}
                <InlineCode>@trustoo/components/Switch/v2</InlineCode> (net-new — no v1 barrel yet).
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Sizes</h3>
                <div className="flex flex-wrap items-center gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {(['sm', 'md'] as SwitchSize[]).map((size) => (
                        <div key={size} className="flex items-center gap-spacing-lg">
                            <Switch size={size} defaultChecked />
                            <Switch size={size} />
                            <span className="text-xs font-medium uppercase text-text-tertiary">
                                {size}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <div>
                    <h3 className="text-sm font-semibold text-text-primary">Default vs Slim</h3>
                    <p className="mt-spacing-xs text-xs text-text-tertiary">
                        <InlineCode>slim</InlineCode> uses a thin bordered rail with a full-height
                        bordered knob; the default rail insets the knob by 2px.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <div className="flex items-center gap-spacing-lg">
                        <Switch defaultChecked />
                        <Switch />
                        <span className="text-xs font-medium text-text-tertiary">Default</span>
                    </div>
                    <div className="flex items-center gap-spacing-lg">
                        <Switch slim defaultChecked />
                        <Switch slim />
                        <span className="text-xs font-medium text-text-tertiary">Slim</span>
                    </div>
                </div>
            </div>

            <div className="space-y-spacing-md">
                <div>
                    <h3 className="text-sm font-semibold text-text-primary">States</h3>
                    <p className="mt-spacing-xs text-xs text-text-tertiary">
                        Hover and <InlineCode>:focus-visible</InlineCode> (tab onto a switch to see
                        the ring) are native; <InlineCode>disabled</InlineCode> mutes both rail and
                        knob.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <Switch />
                    <Switch defaultChecked />
                    <Switch disabled />
                    <Switch disabled defaultChecked />
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">
                    With label &amp; supporting text
                </h3>
                <div className="flex flex-col gap-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <Switch
                        defaultChecked
                        label="Remember me"
                        supportingText="Save my login details for next time."
                    />
                    <Switch
                        size="sm"
                        label="Email notifications"
                        supportingText="Get notified when a new lead matches your profile."
                    />
                    <Switch
                        disabled
                        label="Two-factor authentication"
                        supportingText="Managed by your organisation."
                    />
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={SWITCH_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>checked</InlineCode> /{' '}
                    <InlineCode>defaultChecked</InlineCode> + <InlineCode>onChange</InlineCode>{' '}
                    (controlled or uncontrolled), <InlineCode>size</InlineCode> (sm · md),{' '}
                    <InlineCode>slim</InlineCode>, <InlineCode>label</InlineCode>,{' '}
                    <InlineCode>supportingText</InlineCode>, and <InlineCode>disabled</InlineCode>.
                    Renders a <InlineCode>&lt;button role=&quot;switch&quot;&gt;</InlineCode>; a
                    provided <InlineCode>label</InlineCode> is associated via{' '}
                    <InlineCode>htmlFor</InlineCode> so clicking it toggles too.
                </p>
            </div>
        </div>
    </Section>
)
